"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { toSuperscript } from "@/lib/tool-kit/math/primes";
import { formatFraction, type Rational } from "@/lib/tool-kit/math/rational";
import {
  coefficientDigits,
  coefficientSum,
  type Expansion,
  expand,
  formatBinomialTerm,
  formatExpansion,
  formatTerm,
  MAX_POWER,
  mainVariable,
  parseTerm,
  type Term,
  type TermRead,
  termBreakdown,
  termWithPower,
} from "@/lib/tool-kit/number-theory/binomial";
import { scientific } from "@/lib/tool-kit/number-theory/geometric-sequence";

/** English strings. A translated content file overrides these through `ui`. */
export const BINOMIAL_EXPANSION_CALCULATOR_STRINGS = {
  first: "First term, a",
  firstHint:
    "A number, a variable, or both: 2x, −3y, x^2, 1/2, x^-1. One letter only.",
  second: "Second term, b",
  power: "Power, n",
  powerHint: "A whole number from 0 to {max}.",
  termNumber: "Show term number (optional)",
  termNumberHint: "From 1 to n + 1, counting from the first term.",
  findPower: "Coefficient of {v}^p (optional)",
  findPowerHint: "Enter p. Use 0 for the term without {v}.",
  results: "Results",
  expansion: "Expansion of ({pair}){sup}",
  terms: "Terms after combining",
  coefficientSum: "Sum of the coefficients",
  copyResult: "Copy expansion",
  copied: "Copied!",
  longNote:
    "The coefficients are very long, so they are shown rounded in scientific notation. The exact values are in the copied expansion.",
  generalHeading: "General term",
  generalFormula:
    "T(k + 1) = C({n}, k) · ({a})^({n} − k) · ({b})^k, for k = 0, 1, …, {n}",
  sumCheck:
    "Setting every variable to 1 turns the expansion into a number: ({a1} + {b1}){sup} = {sum}.",
  specificHeading: "Term {r}",
  specificWorking:
    "T({r}) = C({n}, {k}) · ({a}){ak} · ({b}){bk} = {binomial} × {aPart} × {bPart} = {product}",
  powerFound:
    "The coefficient of {v}{sup} is {coefficient}; the term is {term}.",
  powerNone: "No term has {v} to the power {p}.",
  noVariable:
    "Neither term contains a variable, so there is no power to search for.",
  tableHeading: "Term by term",
  colK: "k",
  colBinomial: "C(n, k)",
  colA: "a part",
  colB: "b part",
  colTerm: "Term",
  enter: "Enter both terms and a power.",
  badTerm:
    "“{text}” is not a term this calculator reads. Use a coefficient and/or one letter with a whole-number power, such as 2x, −3y^2 or 1/2.",
  tooLong: "Use numbers of at most 30 digits.",
  bigPower: "A variable's own power is limited to 20 in either direction.",
  badPower: "The power n must be a whole number from 0 to {max}.",
  badTermNumber: "The term number must be a whole number from 1 to {last}.",
  badFindPower: "Enter a whole number for the power p.",
} as const;

type Strings = typeof BINOMIAL_EXPANSION_CALCULATOR_STRINGS;
type Format = (value: Rational) => string;

const LONG_DIGITS = 60;
const MONO_LINES =
  "space-y-1 break-words rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";
const PROSE_SM = "text-muted-foreground text-sm";

/** "x + 3", or "x − 2" when the second term is negative. */
function binomialLabel(a: Term, b: Term): string {
  const second = formatBinomialTerm(b);
  return second.startsWith("−")
    ? `${formatBinomialTerm(a)} − ${second.slice(1)}`
    : `${formatBinomialTerm(a)} + ${second}`;
}

function bracketNegative(text: string): string {
  return text.startsWith("−") ? `(${text})` : text;
}

function termError(
  read: TermRead,
  raw: string,
  strings: Strings
): string | null {
  switch (read.kind) {
    case "ok":
      return null;
    case "too-long":
      return strings.tooLong;
    case "big-power":
      return strings.bigPower;
    default:
      return t(strings.badTerm, { text: raw.trim() });
  }
}

function SpecificTerms({
  a,
  b,
  n,
  expansion,
  raw,
  format,
  strings,
}: {
  a: Term;
  b: Term;
  n: number;
  expansion: Expansion;
  raw: { termNumber: string; findPower: string };
  format: Format;
  strings: Strings;
}) {
  const lines: React.ReactNode[] = [];
  const rText = raw.termNumber.trim();
  if (rText !== "") {
    const r = parseIntegerInput(rText);
    if (r === null || r < 1 || r > n + 1) {
      lines.push(
        <ErrorNotice key="r">
          {t(strings.badTermNumber, { last: String(n + 1) })}
        </ErrorNotice>
      );
    } else {
      const k = r - 1;
      const part = termBreakdown(a, b, n, k);
      const text = (p: {
        coefficient: Rational;
        powers: Record<string, number>;
      }) => formatTerm(p.coefficient, p.powers, format);
      lines.push(
        <div className="space-y-2" key="r">
          <h3 className={WIDGET_H3}>
            {t(strings.specificHeading, { r: String(r) })}
          </h3>
          <div className={MONO_LINES}>
            <p>
              {t(strings.specificWorking, {
                r: String(r),
                n: String(n),
                k: String(k),
                a: formatBinomialTerm(a),
                b: formatBinomialTerm(b),
                ak: toSuperscript(n - k),
                bk: toSuperscript(k),
                binomial: String(part.binomial),
                aPart: text(part.aPart),
                bPart: text(part.bPart),
                product: text(part.product),
              })}
            </p>
          </div>
        </div>
      );
    }
  }
  const pText = raw.findPower.trim();
  const variable = mainVariable(a, b);
  if (pText !== "") {
    const p = parseIntegerInput(pText);
    if (variable === null) {
      lines.push(<Notice key="p">{strings.noVariable}</Notice>);
    } else if (p === null) {
      lines.push(<ErrorNotice key="p">{strings.badFindPower}</ErrorNotice>);
    } else {
      const found = termWithPower(expansion, variable, p);
      const values = { v: variable, p: String(p), sup: toSuperscript(p) };
      lines.push(
        <p className="text-foreground text-sm" key="p">
          {found
            ? t(strings.powerFound, {
                ...values,
                coefficient: format(found.coefficient),
                term: formatTerm(found.coefficient, found.powers, format),
              })
            : t(strings.powerNone, values)}
        </p>
      );
    }
  }
  return <>{lines}</>;
}

function Results({
  a,
  b,
  n,
  raw,
  strings,
}: {
  a: Term;
  b: Term;
  n: number;
  raw: { termNumber: string; findPower: string };
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const expansion = useMemo(() => expand(a, b, n), [a, b, n]);
  const widest = Math.max(
    1,
    ...expansion.parts.map((part) => coefficientDigits(part.coefficient))
  );
  const long = widest > LONG_DIGITS;
  const format: Format = long
    ? (value) =>
        coefficientDigits(value) > LONG_DIGITS
          ? scientific(value)
          : formatFraction(value)
    : formatFraction;
  const exact = formatExpansion(expansion.terms);
  const shown = formatExpansion(expansion.terms, format);
  const sum = coefficientSum(expansion);
  const names = {
    a: formatBinomialTerm(a),
    b: formatBinomialTerm(b),
    n: String(n),
    sup: toSuperscript(n),
    pair: binomialLabel(a, b),
  };
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.expansion, names)}
          value={
            <span className="break-words font-mono text-base">{shown}</span>
          }
        />
        <StatCard label={strings.terms} value={fmt(expansion.terms.length)} />
        <StatCard label={strings.coefficientSum} value={format(sum)} />
      </StatGrid>
      {long ? <Notice>{strings.longNote}</Notice> : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={exact}
      />
      <h3 className={WIDGET_H3}>{strings.generalHeading}</h3>
      <div className={MONO_LINES}>
        <p>{t(strings.generalFormula, names)}</p>
      </div>
      <p className={PROSE_SM}>
        {t(strings.sumCheck, {
          a1: formatFraction(a.coefficient),
          b1: bracketNegative(formatFraction(b.coefficient)),
          sup: names.sup,
          sum: format(sum),
        })}
      </p>
      <SpecificTerms
        a={a}
        b={b}
        expansion={expansion}
        format={format}
        n={n}
        raw={raw}
        strings={strings}
      />
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[
          strings.colK,
          strings.colBinomial,
          strings.colA,
          strings.colB,
          strings.colTerm,
        ]}
        rows={expansion.parts.map((part) => {
          const breakdown = termBreakdown(a, b, n, part.k);
          return [
            part.k,
            String(part.binomial),
            formatTerm(
              breakdown.aPart.coefficient,
              breakdown.aPart.powers,
              format
            ),
            formatTerm(
              breakdown.bPart.coefficient,
              breakdown.bPart.powers,
              format
            ),
            formatTerm(part.coefficient, part.powers, format),
          ];
        })}
      />
    </>
  );
}

function Outcome({
  fields,
  strings,
}: {
  fields: {
    a: string;
    b: string;
    n: string;
    termNumber: string;
    findPower: string;
  };
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const a = parseTerm(fields.a);
  const b = parseTerm(fields.b);
  const n = parseIntegerInput(fields.n);
  if (a.kind === "empty" || b.kind === "empty" || fields.n.trim() === "") {
    return <Notice>{strings.enter}</Notice>;
  }
  const error =
    termError(a, fields.a, strings) ?? termError(b, fields.b, strings);
  if (error) {
    return <ErrorNotice>{error}</ErrorNotice>;
  }
  if (
    n === null ||
    n < 0 ||
    n > MAX_POWER ||
    a.kind !== "ok" ||
    b.kind !== "ok"
  ) {
    return (
      <ErrorNotice>{t(strings.badPower, { max: fmt(MAX_POWER) })}</ErrorNotice>
    );
  }
  return (
    <Results
      a={a.term}
      b={b.term}
      n={n}
      raw={{ termNumber: fields.termNumber, findPower: fields.findPower }}
      strings={strings}
    />
  );
}

export default function BinomialExpansionCalculatorWidget() {
  const strings = useToolUi(BINOMIAL_EXPANSION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [a, setA] = useState("2x");
  const [b, setB] = useState("3");
  const [n, setN] = useState("5");
  const [termNumber, setTermNumber] = useState("3");
  const [findPower, setFindPower] = useState("");
  const parsedA = parseTerm(a);
  const parsedB = parseTerm(b);
  const variable =
    parsedA.kind === "ok" && parsedB.kind === "ok"
      ? mainVariable(parsedA.term, parsedB.term)
      : null;
  const v = variable ?? "x";
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          hint={strings.firstHint}
          id="bec-a"
          label={strings.first}
          mono
          onChange={setA}
          value={a}
        />
        <TextField
          id="bec-b"
          label={strings.second}
          mono
          onChange={setB}
          value={b}
        />
        <TextField
          hint={t(strings.powerHint, { max: fmt(MAX_POWER) })}
          id="bec-n"
          label={strings.power}
          mono
          numeric
          onChange={setN}
          value={n}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.termNumberHint}
          id="bec-term"
          label={strings.termNumber}
          mono
          numeric
          onChange={setTermNumber}
          value={termNumber}
        />
        <TextField
          hint={t(strings.findPowerHint, { v })}
          id="bec-power"
          label={t(strings.findPower, { v })}
          mono
          numeric
          onChange={setFindPower}
          value={findPower}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome
          fields={{ a, b, n, termNumber, findPower }}
          strings={strings}
        />
      </section>
    </>
  );
}
